import Icon from '@atoms/Icon/Icon';
import { ILink } from '@models/ILink';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface Props {
  readonly links: ILink[];
  readonly onClick: (params?: string) => void;
  readonly dataTestId?: string;
}

const LinksList = ({ links, onClick, dataTestId }: Props) => {
  const isInIFrame = useIsInIFrame();
  const isTouchDevice = useIsTouchDevice();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [defaultListRef, { width: defaultListWidth }] = useResizeObserver();

  return (
    <div className="relative flex h-full justify-start lgm:w-4/12 lgm:justify-center">
      <ul
        className="pt-14"
        data-testid={dataTestId}
        style={{ width: defaultListWidth + 10 }}
      >
        {links?.length > 0
          ? links.map(({ name, url, target }, index) => (
              <motion.li
                key={`subNav-link-list--${name}`}
                className="mb-6 flex"
                onMouseEnter={() => {
                  if (isTouchDevice) return;
                  setHoveredIndex(index);
                }}
                onMouseLeave={() => {
                  if (isTouchDevice) return;
                  setHoveredIndex(null);
                }}
                initial={{ x: 0 }}
                animate={{
                  x: hoveredIndex === index ? 10 : 0,
                  transition: {
                    ease: 'easeOut',
                  },
                }}
              >
                <motion.div
                  className="mr-4 flex h-7 w-7 items-center justify-center rounded-full bg-yellow"
                  data-testid="Link-List-Icon"
                  initial={{ opacity: 1 }}
                  animate={{
                    opacity:
                      hoveredIndex === index || hoveredIndex === null ? 1 : 0.7,
                  }}
                >
                  <Icon name="east" size={IconSizeEnum.sm} />
                </motion.div>

                <a
                  href={url}
                  target={isInIFrame ? '_parent' : target}
                  className={classNames(
                    'body-two transition-colors',
                    hoveredIndex === index || hoveredIndex === null
                      ? 'text-black-100'
                      : 'text-grey-450',
                    {
                      'body-two--bold': hoveredIndex === index,
                    }
                  )}
                  onClick={() => onClick(name)}
                  data-testid="Link-List-Link"
                >
                  {name}
                </a>
              </motion.li>
            ))
          : null}
      </ul>

      {/* Because we need to change the font weight of the text inside the list and because this change increases the list width, 
      showing a small flicker, we created some sort of a copy of the displayed list, invisible but taking the same amount of space, 
      so we can compute the original width and always add a 10px buffer to this width for the original list 
      so we have all the space needed for when the font weight is changed. */}
      <ul ref={defaultListRef} className="invisible absolute top-0 z-[1] pt-14">
        {links?.length > 0
          ? links.map(({ name }) => (
              <li key={`subNav-link-list--${name}`} className="mb-6 flex">
                <div className="mr-4 flex h-7 w-7 items-center justify-center rounded-full bg-yellow">
                  <Icon name="east" size={IconSizeEnum.sm} />
                </div>

                <div className="body-two">{name}</div>
              </li>
            ))
          : null}
      </ul>
    </div>
  );
};

export default LinksList;
